import * as THREE from 'three'
import { IntegerProblem, CanonicalCut, getCutInfo } from '@/core/cuts'
import { PolyhedronManager, computeGroupVolume } from '@/graphics/polyhedron-manager'

export interface ApplyCutMetrics {
  newVolume: number
  volumeReductionPct: number
  currentObjectiveValue: number
  objectiveReductionPct: number
  optimalSolution: number[]
}

export interface ApplyCutResult {
  problem: IntegerProblem
  group: THREE.Group
  metrics: ApplyCutMetrics
}

export class CutService {
  private poly: PolyhedronManager

  constructor(polyManager: PolyhedronManager) {
    this.poly = polyManager
  }

  applyCut(problem: IntegerProblem, cut: CanonicalCut, group: THREE.Group | null, originalVolume: number, initialObjectiveValue: number): ApplyCutResult {
    if (!problem || !problem.A || !problem.b || !problem.c || !problem.inequalities) {
      throw new Error('Invalid problem passed to CutService.applyCut')
    }
    if (!cut || !Array.isArray(cut.alpha) || typeof cut.beta !== 'number') {
      throw new Error('Invalid cut passed to CutService.applyCut')
    }
    if (!isFinite(cut.beta) || cut.alpha.some(a => !isFinite(a))) {
      throw new Error('Cut contains non-finite coefficients')
    }
    // Extend to 3D if needed
    const alpha3D = cut.alpha.length === 2 ? [...cut.alpha, 0] : cut.alpha.slice(0, 3)

    // Avoid adding duplicate cuts if the caller already appended it
    const alreadyExists = problem.A.some((row, i) => {
      if (row.length !== alpha3D.length) return false
      if (problem.b[i] !== cut.beta) return false
      for (let j = 0; j < row.length; j++) {
        if (row[j] !== alpha3D[j]) return false
      }
      return true
    })

    const updated: IntegerProblem = alreadyExists
      ? {
          A: problem.A,
          b: problem.b,
          c: problem.c,
          inequalities: problem.inequalities,
        }
      : {
          A: [...problem.A, alpha3D],
          b: [...problem.b, cut.beta],
          c: problem.c,
          inequalities: [...problem.inequalities, { alpha: alpha3D, beta: cut.beta }],
        }

    // Rebuild geometry
    if (group) this.poly.disposeGroup(group)
    const newGroup = this.poly.buildGroupFromInequalities(updated.inequalities)
    this.poly.addIntegerPointsAndAxes(newGroup, updated, { showHull: false })

    // Metrics
    const newVolume = computeGroupVolume(newGroup)
    const volumeReductionPct = originalVolume > 0 ? ((originalVolume - newVolume) / originalVolume) * 100 : 0

    const cutInfo = getCutInfo(updated)
    const currentObjectiveValue = cutInfo ? cutInfo.optimalValue : 0
    const optimalSolution = cutInfo ? cutInfo.optimalSolution : []
    const objectiveReductionPct = initialObjectiveValue > 0 ? ((initialObjectiveValue - currentObjectiveValue) / initialObjectiveValue) * 100 : 0

    return {
      problem: updated,
      group: newGroup,
      metrics: {
        newVolume,
        volumeReductionPct,
        currentObjectiveValue,
        objectiveReductionPct,
        optimalSolution,
      },
    }
  }
}
